import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import LanguageSwitcher from "./LanguageSwitcher";
import { LogOut, User } from "lucide-react";

const LandingHeader = () => {
  const { t } = useTranslation();
  const { user, signOut, loading } = useAuth();

  const handleSignOut = async () => {
    try {
      // 1. Manually clear Supabase tokens from localStorage first
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith("sb-") || key.includes("supabase")) {
          localStorage.removeItem(key);
        }
      });

      // 2. Try official sign out with timeout (max 1 second)
      // This prevents the button from feeling "broken" if the API call hangs
      await Promise.race([
        signOut(),
        new Promise(resolve => setTimeout(resolve, 1000))
      ]);
      
      // 3. Force reload to home
      window.location.href = "/";
    } catch (error) {
      console.error("Logout error:", error);
      
      // Fallback: Clear storage again and reload
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith("sb-") || key.includes("supabase")) {
          localStorage.removeItem(key);
        }
      });
      window.location.href = "/";
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-foreground/10">
      <div className="container mx-auto px-4 md:px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-6">
            <Link to="/" className="logo">
              Vesper
            </Link>
            {/* 星語 Entry - Dashboard shortcut */}
            <Link 
              to={user ? "/dashboard" : "/login"} 
              className="
                inline-flex items-center gap-2
                px-3 sm:px-4 py-2 bg-[#FFD93D] border-2 border-black rounded-lg
                font-semibold text-sm
                hover:translate-x-0.5 hover:translate-y-0.5
                hover:shadow-[4px_4px_0_#1A1A1A]
                transition-all duration-100
              "
            >
              {t("header.vesper")}
            </Link>
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center gap-2 md:gap-6">
            <a href="/#pricing" className="btn-ghost hidden sm:inline-flex text-sm px-4">
              {t("header.pricing")}
            </a>
            
            {loading ? (
              <div className="w-16 h-8 bg-muted animate-pulse rounded-full"></div>
            ) : user ? (
              <div className="flex items-center gap-2 md:gap-4">
                <div className="hidden md:flex items-center gap-2 bg-secondary/50 px-3 py-2 rounded-full">
                  <User className="w-4 h-4" />
                  <span className="text-sm font-medium truncate max-w-[120px]">
                    {user.email?.split("@")[0]}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="btn-outline text-sm py-2 px-3"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline">{t("header.logout")}</span>
                </button>
              </div>
            ) : (
              <Link to="/login" className="btn-primary text-sm py-2 px-4">
                {t("header.login")}
              </Link>
            )}

            <LanguageSwitcher />

          </div>
        </div>
      </div>
    </header>
  );
};

export default LandingHeader;